/**
 * Forgot Password Page
 * ====================
 * Password reset request page.
 *
 * UI mirrors the Login page: the same soft light backdrop (grid + brand blobs),
 * elevated white card, AuthLogo / BrandWordmark logo treatment, and accent submit
 * button — so the auth flow feels like one continuous experience.
 */

import React, { useState, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import authAPI from '../api/auth';
import { useOrgBranding } from '../hooks/useOrgBranding';
import { useDocumentTitle } from '../hooks';
import { DEFAULT_BRAND_NAME } from '../utils/constants';
import AuthLogo from '../components/auth/AuthLogo';
import BrandWordmark from '../components/auth/BrandWordmark';
import { Button, Input, Alert } from '../components/ui';
import Captcha, { captchaEnabled } from '../components/Captcha';

const ForgotPassword = () => {
  const { orgSlug } = useParams();
  const [email, setEmail] = useState('');
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [apiError, setApiError] = useState(null);
  const [captchaToken, setCaptchaToken] = useState(null);
  const [captchaUnavailable, setCaptchaUnavailable] = useState(false);
  const [captchaKey, setCaptchaKey] = useState(0);
  const { branding: orgBranding, resolved: brandingResolved } = useOrgBranding(orgSlug);
  const brandName = orgBranding?.name || DEFAULT_BRAND_NAME;
  useDocumentTitle(`${brandName} Admin Portal`);

  // Show the org's own logo/name when it has branding; otherwise fall back to
  // the default product wordmark so the page always carries a logo (matches Login).
  const hasOrgBranding = Boolean(orgBranding?.logo_url || orgBranding?.name);

  const loginHref = orgSlug ? `/${orgSlug}/login` : '/login';

  // Stable callbacks so the Captcha effect doesn't re-render the widget on every keystroke.
  const handleCaptchaVerify = useCallback((token) => setCaptchaToken(token), []);
  const handleCaptchaExpire = useCallback(() => setCaptchaToken(null), []);
  const handleCaptchaLoadError = useCallback(() => setCaptchaUnavailable(true), []);

  const validateForm = () => {
    const newErrors = {};

    if (!email) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setApiError(null);

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      await authAPI.forgotPassword(email, captchaToken);
      setSubmitted(true);
    } catch (error) {
      console.error('Forgot password error:', error);
      setApiError(
        error.code === 'CAPTCHA_FAILED'
          ? 'CAPTCHA verification failed. Please complete the challenge and try again.'
          : error.message || 'Failed to send reset instructions. Please try again.'
      );
    } finally {
      setLoading(false);
      // Single-use v2 token: clear and remount the widget after every attempt.
      if (captchaEnabled) {
        setCaptchaToken(null);
        setCaptchaKey((k) => k + 1);
      }
    }
  };

  // Shared soft brand backdrop — identical to the Login page.
  const backdrop = (
    <div className="pointer-events-none absolute inset-0" aria-hidden="true">
      <div className="absolute inset-0 bg-[linear-gradient(rgba(0,125,136,0.055)_1px,transparent_1px),linear-gradient(90deg,rgba(0,125,136,0.055)_1px,transparent_1px)] bg-[size:28px_28px]" />
      <div className="absolute -top-40 left-1/4 h-[32rem] w-[42rem] -translate-x-1/2 rounded-full bg-primary/20 blur-[130px]" />
      <div className="absolute -bottom-40 right-0 h-[28rem] w-[32rem] translate-x-1/4 rounded-full bg-accent/20 blur-[120px]" />
      <div className="absolute top-1/2 left-1/2 h-72 w-72 -translate-x-1/2 -translate-y-1/2 rounded-full bg-primary/10 blur-[110px]" />
    </div>
  );

  // Logo block — matches Login: org logo when branded, product wordmark otherwise.
  const logo = (
    <div className="mb-8 flex justify-center">
      {!brandingResolved || hasOrgBranding ? (
        <AuthLogo branding={orgBranding} resolved={brandingResolved} variant="light" />
      ) : (
        <Link to="/" aria-label={DEFAULT_BRAND_NAME}>
          <BrandWordmark tone="dark" size="lg" />
        </Link>
      )}
    </div>
  );

  const footer = (
    <p className="mt-8 text-center text-sm text-neutral-400">
      {brandName} &copy; {new Date().getFullYear()} All rights reserved.
    </p>
  );

  if (submitted) {
    return (
      <div className="relative flex min-h-screen flex-col items-center justify-center overflow-hidden bg-neutral-50 px-4 py-12">
        {backdrop}

        <div className="relative w-full max-w-md animate-slide-up">
          <div className="overflow-hidden rounded-2xl bg-white shadow-strong ring-1 ring-neutral-900/5">
            <div className="px-8 py-10 sm:px-10">
              {logo}

              {/* Icon */}
              <div className="mb-6 flex justify-center">
                <div className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
                  <svg className="h-8 w-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
              </div>

              <h1 className="text-center text-2xl font-bold text-neutral-900">
                Check your email
              </h1>
              <p className="mt-2 mb-8 text-center text-neutral-500">
                We've sent password reset instructions to <strong>{email}</strong>
              </p>

              {/* Instructions */}
              <div className="mb-6 rounded-lg border border-blue-200 bg-blue-50 p-4">
                <h3 className="mb-2 font-semibold text-blue-900">Next steps:</h3>
                <ul className="space-y-2 text-sm text-blue-800">
                  <li className="flex items-start">
                    <svg className="mr-2 mt-0.5 h-5 w-5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    Check your email inbox (and spam folder)
                  </li>
                  <li className="flex items-start">
                    <svg className="mr-2 mt-0.5 h-5 w-5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    Click the reset link in the email
                  </li>
                  <li className="flex items-start">
                    <svg className="mr-2 mt-0.5 h-5 w-5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    Create a new password
                  </li>
                </ul>
              </div>

              <p className="mb-6 text-center text-sm text-neutral-500">
                Didn't receive the email? Check your spam folder or wait a few minutes.
              </p>

              <div className="text-center">
                <Link
                  to={loginHref}
                  className="text-sm font-medium text-primary hover:text-primary-600"
                >
                  Back to login
                </Link>
              </div>
            </div>
          </div>

          {footer}
        </div>
      </div>
    );
  }

  return (
    <div className="relative flex min-h-screen flex-col items-center justify-center overflow-hidden bg-neutral-50 px-4 py-12">
      {backdrop}

      <div className="relative w-full max-w-md animate-slide-up">
        <div className="overflow-hidden rounded-2xl bg-white shadow-strong ring-1 ring-neutral-900/5">
          <div className="px-8 py-10 sm:px-10">
            {logo}

            {/* Heading */}
            <h1 className="text-center text-2xl font-bold text-neutral-900">
              Forgot your password?
            </h1>
            <p className="mt-2 mb-8 text-center text-neutral-500">
              Enter your email address and we'll send you instructions to reset your {brandName} password.
            </p>

            {/* Error Alert */}
            {apiError && (
              <div className="mb-6">
                <Alert variant="error" title="Error">
                  {apiError}
                </Alert>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              <Input
                type="email"
                id="email"
                name="email"
                label="Email Address"
                required
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                error={errors.email}
                disabled={loading}
                autoFocus
              />

              {/* CAPTCHA */}
              {captchaEnabled && (
                <div>
                  <Captcha
                    key={captchaKey}
                    onVerify={handleCaptchaVerify}
                    onExpire={handleCaptchaExpire}
                    onLoadError={handleCaptchaLoadError}
                  />
                  {captchaUnavailable && (
                    <p className="mt-2 text-sm text-neutral-500 text-center">
                      CAPTCHA couldn't load. You can still continue.
                    </p>
                  )}
                </div>
              )}

              {/* Submit Button */}
              <div className="pt-2">
                <Button
                  type="submit"
                  variant="accent"
                  size="lg"
                  loading={loading}
                  disabled={loading || (captchaEnabled && !captchaToken && !captchaUnavailable)}
                  className="w-full"
                >
                  {loading ? 'Sending...' : 'SEND RESET INSTRUCTIONS'}
                </Button>
              </div>

              <div className="text-center">
                <Link
                  to={loginHref}
                  className="text-sm font-medium text-primary hover:text-primary-600"
                >
                  Back to login
                </Link>
              </div>
            </form>
          </div>
        </div>

        {footer}
      </div>
    </div>
  );
};

export default ForgotPassword;
